import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { memo, useEffect, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import ItemShelf from '@/components/ItemShelf/ItemShelf';
import { RecommendationVisibilityWrapper } from '@/pages/Item/ItemPage/components/ItemPageCarousel/RecommendationVisibilityWrapper';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserDataLoading } from '@/redux/modules/account/user/user.selectors';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchRecommendedItemIfNeeded, getSortedRecommendedItemIdsFromState } from '@/redux/modules/recommendedItem';
import { GlobalState } from '@/redux/store';
import { LotRecommendationType } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { getRecommendedItemsUrl } from '@/utils/urls';

const MIN_ITEMS_FOR_SEE_ALL = 6;

type Props = {
    className?: string;
};

const RecommendedItemCarousel = ({ className }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const itemIds = useAppSelector((state: GlobalState) => getSortedRecommendedItemIdsFromState(state, 12));
    const userDataLoading = useAppSelector(isUserDataLoading);
    const prevUserDataLoading = usePrevious(userDataLoading);
    const [visibleItemIds, setVisibleItemIds] = useState<number[]>([]);

    useEffect(() => {
        if (!userDataLoading && prevUserDataLoading === true) {
            dispatch(fetchRecommendedItemIfNeeded(false));
        }
    }, [dispatch, userDataLoading, prevUserDataLoading]);

    useDeepCompareEffect(() => {
        if (itemIds.length) {
            dispatch(
                fetchItemSummariesIfNeeded({
                    identifier: 'RECOMMENDED-ITEM-Carousel',
                    itemIds,
                })
            );
        }
    }, [dispatch, itemIds]);

    if (!itemIds.length) {
        return null;
    }

    return (
        <RecommendationVisibilityWrapper
            recommendationIds={visibleItemIds}
            recommendationType={LotRecommendationType.RecommendedItems}
        >
            <ItemShelf
                className={className}
                cta={itemIds.length > MIN_ITEMS_FOR_SEE_ALL}
                identifier="recommendedItem"
                itemIds={itemIds}
                onVisibleItemIdsChange={setVisibleItemIds}
                seeAllUrl={getRecommendedItemsUrl()}
                showPagination={false}
                testid="recommendedItemCarousel"
                title={formatMessage({ id: 'recommended_items' })}
            />
        </RecommendationVisibilityWrapper>
    );
};

export default memo<Props>(RecommendedItemCarousel);
